"use client"

import { observer } from "mobx-react-lite"
import { Bar, BarChart, CartesianGrid, ReferenceLine, XAxis, YAxis } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface DivergingBarChartProps {
  title: string
  description?: string
  data: Array<{
    label: string
    positive: number
    negative: number
  }>
  positiveLabel: string
  negativeLabel: string
  theme?: ThemeConfigType
  className?: string
}

export const DivergingBarChart = observer(
  ({
    title,
    description,
    data,
    positiveLabel,
    negativeLabel,
    theme,
    className,
  }: DivergingBarChartProps) => {
    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme

    // Format large numbers (e.g., 100000 -> 0.1m)
    const formatYAxis = (value: number): string => {
      const absValue = Math.abs(value)
      if (absValue >= 1000000) {
        return `${(absValue / 1000000).toFixed(1)}m`
      }
      if (absValue >= 1000) {
        return `${(absValue / 1000).toFixed(1)}k`
      }
      return absValue.toString()
    }

    // Chart config for legend
    const chartConfig: ChartConfig = {
      positive: {
        label: positiveLabel,
        color: "var(--chart-3)", // Blue for additions
      },
      negative: {
        label: negativeLabel,
        color: "var(--chart-4)", // Orange for deletions
      },
    }

    // Find max value for axis scaling
    const maxValue = Math.max(
      ...data.map((d) => Math.max(Math.abs(d.positive), Math.abs(d.negative))),
      1, // Ensure minimum of 1 to avoid zero domain
    )
    // Symmetric domain around 0 to keep 0 at center
    const yAxisDomain: [number, number] = [-maxValue * 1.1, maxValue * 1.1]

    // Generate clean tick values that include 0
    const generateTicks = (): number[] => {
      const roundedMax = Math.ceil(maxValue / 1000) * 1000 // Round up to nearest 1000
      const step = roundedMax / 2 // Create 2 ticks above and below 0
      return [-roundedMax, -step, 0, step, roundedMax]
    }

    return (
      <Card
        className={cn(
          "w-full overflow-hidden",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        <CardHeader>
          <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>{title}</CardTitle>
          {description && (
            <p
              className={cn(
                "text-sm text-muted-foreground",
                !isDefaultTheme && themeClasses.typography,
              )}
            >
              {description}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={chartConfig}
            className="h-[300px] sm:h-[350px] md:h-[400px] w-full"
          >
            <BarChart data={data} accessibilityLayer>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tickLine={false}
                tickMargin={8}
                axisLine={false}
                tick={{ fontSize: "12px" }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: "12px" }}
                domain={yAxisDomain}
                ticks={generateTicks()}
                tickFormatter={formatYAxis}
              />
              <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={2} />
              <ChartTooltip
                content={<ChartTooltipContent />}
                formatter={(value: number, name: string) => {
                  const displayValue = Math.abs(value)
                  const label = name === "positive" ? positiveLabel : negativeLabel
                  return [`${displayValue.toLocaleString()}`, label]
                }}
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar dataKey="positive" fill="var(--color-positive)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="negative" fill="var(--color-negative)" radius={[0, 0, 4, 4]} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    )
  },
)

DivergingBarChart.displayName = "DivergingBarChart"
